import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import { memo, Component, createRef } from 'react';
import classnames from 'classnames';
import { ArrowDown } from '../../Icons/ArrowDown/index.js';
import '../../Icons/Camera/index.js';
import '../../Icons/Category/index.js';
import '../../Icons/CheckCircle/index.js';
import { Close } from '../../Icons/Close/index.js';
import '../../Icons/Favorite/index.js';
import '../../Icons/Lens/index.js';
import '../../Icons/LensHeart/index.js';
import '../../Icons/Phone/index.js';
import '../../Icons/Pin/index.js';
import '../../Icons/Promo/index.js';
import '../../Icons/Share/index.js';
import '../../Icons/Bell/index.js';
import '../../Icons/Bin/index.js';
import '../../Icons/Check/index.js';
import '../../Icons/TriangleExclamation/index.js';
import '../../Icons/ShoppingBag/index.js';
import { KeyCodes } from '../../lib/constants.js';
import ElementContainer from '../ElementContainer/index.js';
import { InputFooter } from '../InputFooter/index.js';
import { InputHeader } from '../InputHeader/index.js';
import { DummyInput } from './DummyInput.js';
import classes from './index.module.scss.js';

class SelectBoxSkeleton extends Component {
    render() {
        const { flavor, type = 'default', expanded } = this.props;
        return (jsx(ElementContainer, { children: jsxs("div", { className: classnames(classes.wrapper, classes.skeleton, classes[flavor], classes[type]), children: [jsx("div", { className: classes.skeletonElem }), expanded && jsx("div", { className: classes.skeletonElem })] }) }));
    }
}
const Options = memo((props) => {
    const { opts, disabled, selectedOptions, preventAndStop, onSelect, name, allowGroupSelection, } = props;
    const renderOptionContent = (opt) => (jsxs(Fragment, { children: [opt.icon, jsx("p", { children: opt.name })] }));
    const renderGroup = (opt, idx) => (jsx("li", { role: allowGroupSelection ? 'option' : 'presentation', tabIndex: -1, id: `${name}__group--${idx}`, "aria-selected": !!selectedOptions[opt.index], "aria-disabled": opt.disabled, className: classnames(classes.group, {
            [classes.option]: allowGroupSelection,
            [classes.selected]: allowGroupSelection && !!selectedOptions[opt.index],
            [classes.disabled]: opt.disabled,
        }), onClick: !allowGroupSelection || disabled || opt.disabled
            ? undefined
            : (evt) => onSelect(evt, opt), children: renderOptionContent(opt) }, idx));
    const renderOption = (opt, idx) => (jsx("li", { role: "option", tabIndex: -1, id: `${name}__option--${idx}`, "aria-selected": !!selectedOptions[opt.index], "aria-disabled": opt.disabled, className: classnames(classes.option, {
            [classes.selected]: !!selectedOptions[opt.index],
            [classes.disabled]: opt.disabled,
        }), onFocus: preventAndStop, onBlur: preventAndStop, onMouseDown: preventAndStop, onClick: disabled || opt.disabled ? undefined : (evt) => onSelect(evt, opt), children: renderOptionContent(opt) }, idx));
    return (jsx(Fragment, { children: opts.map((opt, idx) => opt.type === 'group' ? renderGroup(opt, idx) : renderOption(opt, idx)) }));
});
Options.displayName = 'Options';
class SelectBox extends Component {
    rootNode = createRef();
    keyIndex = 1;
    options = [];
    valueListCache = [];
    inputDummyRef = null;
    constructor(props) {
        super(props);
        let selectedOptions = {};
        this.updateInternalListAndCache();
        if (!this.isNullable()) {
            const firstSelection = this.getFirstSelectableElement();
            if (firstSelection) {
                selectedOptions[firstSelection.index] = firstSelection;
            }
        }
        if (props.selectedIndices && props.selectedIndices.length) {
            selectedOptions = SelectBox.getSelectionObjectFromIndices(props.selectedIndices, props.valueList);
        }
        this.state = {
            selectedOptions,
            opened: false,
            focused: false,
        };
    }
    static getSelectionObjectFromIndices(indices, valueList) {
        const selectionObject = {};
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore: These two objects are incompatible, but everything appears to be working.
        // Best guess is that the valueList from the props is mutated in the
        // `updateInternalListAndCache` function. As this is the legacy select box,
        // we should remove it asap.
        indices.forEach((index) => (selectionObject[index] = valueList[index]));
        return selectionObject;
    }
    static getDerivedStateFromProps(props, state) {
        if (!props.selectedIndices) {
            return state;
        }
        const selectedOptions = SelectBox.getSelectionObjectFromIndices(props.selectedIndices, props.valueList);
        return {
            selectedOptions,
            opened: state.opened,
            focused: state.focused,
        };
    }
    updateInternalListAndCache = () => {
        const { valueList } = this.props;
        this.options = valueList.map((opt, index) => Object.assign({ index }, opt));
        this.valueListCache = valueList;
    };
    isNullable = () => {
        return this.props.type === 'multi' || this.props.nullable;
    };
    collapse = () => {
        this.setState({
            opened: false,
            focused: false,
        });
    };
    expand = () => {
        this.setState({
            opened: true,
            focused: true,
        });
    };
    toggleExpand = (e) => {
        if (this.props.onClick)
            this.props.onClick(e);
        if (e.defaultPrevented)
            return;
        if (!this.state.opened) {
            this.focusDummyInput();
        }
        this.preventAndStop(e);
        this.setState({
            opened: !this.state.opened,
            focused: !this.state.opened,
        });
    };
    focusDummyInput() {
        this.inputDummyRef?.focus();
    }
    blurDummyInput() {
        this.inputDummyRef?.blur();
    }
    getInputDummyRef = (ref) => {
        this.inputDummyRef = ref;
    };
    onInputDummyBlur = () => {
        //To avoid conflict with others logic, for example the toogleExpand one,
        //We wait before to handle the collapse with blur
        window.setTimeout(() => {
            if (this.rootNode.current &&
                this.rootNode.current.contains(document.activeElement)) {
                this.inputDummyRef?.focus();
                return;
            }
            if (!this.state.opened) {
                return;
            }
            this.collapse();
        }, 10);
    };
    onSelect = (e, option) => {
        this.preventAndStop(e);
        if (option.disabled) {
            return;
        }
        const selectedOptions = this.props.type === 'multi'
            ? Object.assign({}, this.state.selectedOptions)
            : {};
        if (selectedOptions[option.index]) {
            delete selectedOptions[option.index];
        }
        else {
            selectedOptions[option.index] = option;
        }
        this.props.onSelect(this.getSelectionArrayFromObject(selectedOptions));
        this.setState({
            selectedOptions,
            opened: this.props.type === 'multi',
        });
        if (!this.props.avoidFocusOnSelect) {
            this.rootNode.current?.focus();
        }
        this.blurDummyInput();
    };
    getSelectionArrayFromObject = (selectionObject) => {
        return Object.keys(selectionObject).map((optionIndex) => this.props.valueList[Number(optionIndex)]);
    };
    getFirstSelectableElement = () => {
        return this.options.find((opt) => !opt.disabled && opt.type !== 'group');
    };
    onReset = (e) => {
        this.preventAndStop(e);
        const selectedOptions = {};
        if (!this.isNullable()) {
            const firstSelection = this.getFirstSelectableElement();
            if (firstSelection) {
                selectedOptions[firstSelection.index] = firstSelection;
            }
        }
        this.props.onSelect(this.getSelectionArrayFromObject(selectedOptions));
        this.setState({
            selectedOptions,
        });
    };
    handleKeyDown = (e) => {
        switch (e.keyCode) {
            case KeyCodes.Up:
                this.keyIndex = this.keyIndex - 1 < 1 ? 1 : this.keyIndex - 1;
                this.focusListElement();
                break;
            case KeyCodes.Down:
                this.keyIndex =
                    this.keyIndex + 1 > this.props.valueList.length
                        ? this.keyIndex
                        : this.keyIndex + 1;
                this.focusListElement();
                break;
            case KeyCodes.Escape:
                this.setState({ opened: false });
                break;
            case KeyCodes.Tab:
                if (e.target !== this.rootNode.current) {
                    const target = e.target;
                    target.click();
                }
                break;
            case KeyCodes.Enter:
                if (e.target === this.rootNode.current) {
                    this.setState({
                        opened: true,
                        focused: true,
                    });
                }
                else {
                    const target = e.target;
                    target.click();
                }
                break;
        }
    };
    focusListElement = () => {
        const listItem = this.rootNode.current?.querySelector(`li:nth-child(${this.keyIndex})`);
        listItem.focus();
        this.setState({ opened: true });
    };
    addFocus = () => {
        this.setState({ focused: true });
    };
    removeFocus = () => {
        this.setState({ focused: false });
    };
    preventAndStop = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };
    render() {
        const { name, type = 'default', icon, expanded, allowGroupSelection = false, dropdownHeight = 'medium', placeholder, inlineLabel, flavor, valueList, disabled, showAllOptions = false, classes: customClasses = [], inputHeaderProps, errorMessage, } = this.props;
        const { opened, focused, selectedOptions } = this.state;
        const selectionArray = this.getSelectionArrayFromObject(selectedOptions);
        if (valueList !== this.valueListCache) {
            this.updateInternalListAndCache();
        }
        return (jsxs("div", { className: classnames({
                [classes['is-error']]: !!errorMessage,
            }), children: [jsx(InputHeader, { name: name, ...inputHeaderProps, style: errorMessage ? 'red' : 'default' }), jsx("div", { className: classnames({ [classes.container]: !expanded, [classes.visible]: opened }, classes[flavor], ...customClasses), children: jsx(ElementContainer, { hasError: !!errorMessage, active: opened || focused, disabled: disabled, children: jsxs("div", { className: classnames(classes.wrapper, classes[flavor], classes[type], {
                                [classes.expanded]: expanded,
                                [classes.disabledWrapper]: disabled,
                                [classes.hasIcon]: icon,
                                [classes.showAllOptions]: showAllOptions,
                            }), "aria-expanded": opened, tabIndex: 0, onClick: disabled ? undefined : this.toggleExpand, onKeyDown: disabled ? undefined : this.handleKeyDown, onBlur: disabled ? undefined : this.removeFocus, onFocus: disabled ? undefined : this.addFocus, role: "listbox", ref: this.rootNode, children: [jsx("input", { type: "hidden", name: name, value: selectionArray.map((opt) => opt.value).toString() }), jsx(DummyInput, { innerRef: this.getInputDummyRef, onBlur: this.onInputDummyBlur }), jsxs("div", { className: classnames(classes.current, {
                                        [classes.placeholder]: !selectionArray.length,
                                        [classes.inlineLabel]: inlineLabel,
                                    }), children: [inlineLabel && jsx("p", { children: inlineLabel }), jsx("p", { className: classes['selected-option-text'], children: selectionArray.length
                                                ? selectionArray.map((opt) => opt.name).join(', ')
                                                : placeholder }), icon && jsx("div", { className: classes.icon, children: icon }), jsx("div", { className: classnames(classes.arrow, { [classes.rotate]: opened }), children: selectionArray.length && !opened && this.isNullable() ? (jsx(Close, { onClick: disabled ? undefined : this.onReset, onMouseDown: this.preventAndStop })) : (jsx(ArrowDown, {})) })] }), jsx("ul", { role: "listbox", className: classnames(classes.options, {
                                        [classes.visible]: opened,
                                        [classes['dd-minimal']]: dropdownHeight === 'minimal',
                                    }), children: jsx(Options, { allowGroupSelection: allowGroupSelection, opts: this.options, disabled: disabled, selectedOptions: selectedOptions, preventAndStop: this.preventAndStop, onSelect: this.onSelect, name: name }) })] }) }) }), errorMessage && jsx(InputFooter, { message: errorMessage })] }));
    }
}

export { SelectBox, SelectBoxSkeleton };
//# sourceMappingURL=index.js.map
